import Link from "@whoppah/next/link";
import { useTranslation } from "next-i18next";

import { UrlObject } from "url";

import { IconNavigationChevronLeft } from "@/assets/icons/figma/IconNavigationChevronLeft";
import { IconNavigationChevronRight } from "@/assets/icons/figma/IconNavigationChevronRight";
import { useURLPagination } from "@/common/hooks/pages.hooks";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { cn } from "@/common/utils/css.utils";
import { ELLIPSIS, getPaginationRange } from "@/common/utils/pagination.utils";

const itemClass =
  "flex h-10 min-w-10 items-center justify-center rounded-full px-2 font-body text-sm text-black hover:bg-brand-50";

type PaginationProps = {
  /** 1-based current page. */
  page: number;
  /** Total number of pages. */
  pages: number;
  /** Dims the control and stops clicks while the next page is loading. */
  loading?: boolean;
  /**
   * Where a page number points. Defaults to `?page=n` on the current path, dropped on page 1 —
   * which is what most callers want. Pass your own for path-based paging (`/style/p/2`), or to
   * keep other query params across the page change, which is why it may return a url object as
   * well as a string: search hands back `{ pathname, query }` so its facets survive.
   */
  buildHref?: (page: number) => string | UrlObject;
  className?: string;
};

/**
 * The site's page navigation. Every control is a real link, so a crawler can follow it and
 * middle-click works.
 *
 * `hasPrev` / `hasNext` are deliberately not props: they are `page > 1` and `page < pages`, and a
 * caller that could disagree with its own page numbers would be a caller with a bug.
 */
const Pagination = ({
  page,
  pages,
  loading,
  buildHref,
  className,
}: PaginationProps) => {
  const { t } = useTranslation();
  const { getURLString } = useURLPagination();

  const href = buildHref ?? getURLString;

  if (pages <= 1) {
    return null;
  }

  const items = getPaginationRange(page, pages);

  /* Every control here is a real link, so the event has to hang off the click rather than off a
   * page-change effect — the same page can be reached by back/forward, which isn't a pagination
   * click. Reports the page being asked for, as the gateway PLP did. */
  const trackPage = (nextPage: number) => () =>
    analyticsService.trackPaginationList(nextPage);

  return (
    <nav
      aria-label={t("common:common-page")}
      className={cn(
        "flex items-center justify-center space-x-1 pt-8",
        loading && "pointer-events-none opacity-50",
        className
      )}
    >
      {page > 1 && (
        <Link
          href={href(page - 1)}
          onClick={trackPage(page - 1)}
          className={itemClass}
          aria-label={t("common:common-previous")}
        >
          <IconNavigationChevronLeft className="h-5 w-5" />
        </Link>
      )}

      {items.map((item, index) =>
        item === ELLIPSIS ? (
          <span key={`gap-${index}`} className="px-1 text-grey-400">
            …
          </span>
        ) : (
          <Link
            key={item}
            href={href(item)}
            onClick={trackPage(item)}
            aria-current={item === page ? "page" : undefined}
            className={cn(
              itemClass,
              item === page &&
                "pointer-events-none border-2 border-brand-500 font-semibold text-brand-500"
            )}
          >
            {item}
          </Link>
        )
      )}

      {page < pages && (
        <Link
          href={href(page + 1)}
          onClick={trackPage(page + 1)}
          className={itemClass}
          aria-label={t("common:common-next")}
        >
          <IconNavigationChevronRight className="h-5 w-5" />
        </Link>
      )}
    </nav>
  );
};

export default Pagination;
